import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { CirclePlus } from "lucide-react";

import CenteredHeading from "../../../../components/UI/headingsUI/CenteredHeading";
import PersonalInfo from "./PersonalInfo";
import CompanyInfo from "./CompanyInfo";
import BankInfo from "./BankInfo";
import GrayButton from "../../../../components/UI/buttonUI/GrayButton";
import UserViewDetailsDialog from "../../dialogs/UserViewDetailsDialog";
import { getCompressImage } from "../../../../functions/getCompressImage";
import PrimaryButton from "../../../../components/UI/buttonUI/PrimaryButton";
import { ADD_USER_STEP_FIELDS } from "../../utils/addUserStepFields";
import { StepIndicator } from "./StepIndicator";

const AddUserForm = ({ userSetup, sendData, successResponse, loading }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    trigger,
    formState: { errors },
    reset,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      roles: [],
      profile: [],
      id_proofPic: [],
    },
    shouldUnregister: false,
  });

  const totalSteps = [1, 2, 3];
  let pic = watch("profile");
  let IdProofPics = watch("id_proofPic");
  const id_proof = watch("id_proof");
  const profile_pic = watch("profile_pic");

  const { append, remove } = useFieldArray({
    control,
    name: "id_proof",
  });

  const { append: appendProfilePic, remove: removeProfilePic } = useFieldArray({
    control,
    name: "profile_pic",
  });
  useEffect(() => {
    getCompressImage(IdProofPics, append);
  }, [IdProofPics]);

  useEffect(() => {
    if (pic && pic.length !== 0) {
      appendProfilePic(pic[0]);
    }
  }, [pic]);

  const onSubmit = (data) => {
    const updatedData = { ...data, password: "password" };
    sendData(updatedData, reset);
  };

  // Validate current step before going next
  const handleNext = async () => {
    const fieldsToValidate = ADD_USER_STEP_FIELDS[currentStep]?.fields || [];

    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => prev - 1);
  };

  return (
    <div className="form-container">
      <CenteredHeading title="Add User" />

      {/* Step Indicator */}

      <StepIndicator
        currentStep={currentStep}
        totalSteps={totalSteps}
        setCurrentStep={setCurrentStep}
        trigger={trigger}
        completedSteps={completedSteps}
        setCompletedSteps={setCompletedSteps}
      />

      <form onSubmit={!loading ? (e) => handleSubmit(onSubmit)(e) : undefined}>
        {currentStep === 1 && (
          <PersonalInfo
            userSetup={userSetup}
            register={register}
            errors={errors}
            setValue={setValue}
            control={control}
            watch={watch}
            id_proof={id_proof}
            remove={remove}
            removeProfilePic={removeProfilePic}
            profile_pic={profile_pic}
          />
        )}
        {currentStep === 2 && (
          <CompanyInfo
            userSetup={userSetup}
            register={register}
            errors={errors}
            setValue={setValue}
            control={control}
            watch={watch}
            render={successResponse}
          />
        )}
        {currentStep === 3 && (
          <BankInfo
            userSetup={userSetup}
            register={register}
            errors={errors}
            setValue={setValue}
            control={control}
            watch={watch}
          />
        )}
        {/* BUTTONS */}
        <div className="flex justify-center items-center gap-4 pt-8">
          {/* Clear */}
          <GrayButton
            btnText="Cancel"
            width="w-40"
            onClick={() => {
              reset();
              setCurrentStep(1);
              setCompletedSteps([]);
            }}
          />
          {/* Previous */}
          {currentStep > 1 && (
            <GrayButton
              btnText="Previous"
              onClick={handlePrevious}
              disabled={loading}
            />
          )}
          {/* Next */}
          {currentStep < totalSteps.length && (
            <button
              type="button"
              onClick={handleNext}
              disabled={loading}
              className="p-2 w-20 rounded-md bg-dark-blue text-white hover:bg-blue-950"
            >
              Next
            </button>
          )}

          {/* Final Submit */}
          {currentStep === totalSteps.length && (
            <PrimaryButton
              btnText={!loading ? "Add User" : "Adding..."}
              loading={loading}
              Icon={CirclePlus}
              size={18}
            />
          )}
        </div>
      </form>
      {successResponse && (
        <UserViewDetailsDialog
          viewDetails={successResponse}
          successMsg="User Successfully Created!"
        />
      )}
    </div>
  );
};

export default AddUserForm;
